import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  User, Building2, Key, Bell, CreditCard, Shield, Save, Loader2, Eye, EyeOff,
  Trash2, Plus, Check, X, ChevronDown, Clock, AlertTriangle, Copy, RefreshCw,
  UserPlus, Crown, ShieldCheck, Users, Globe, Mail, Phone, MapPin, FileText, ScrollText,
  Filter, Download, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

// ─── Types ──────────────────────────────────────────────
interface Profile {
  id: string; email: string; name: string; avatar?: string; role: string;
  phone?: string; timezone?: string; language?: string;
  twoFactorEnabled?: boolean; lastLoginAt?: string; createdAt?: string;
}

interface OrgData {
  id: string; name: string; slug: string; plan: string;
  website?: string; industry?: string; description?: string; logo?: string;
  address?: string; phone?: string; billingEmail?: string; taxId?: string;
  members: { id: string; name: string; email: string; role: string; avatar?: string; lastLoginAt?: string; createdAt?: string }[];
  _count?: { users: number; campaigns: number; platformAccounts: number };
}

interface NotifPrefs {
  campaignCompleted: boolean; newLeads: boolean; postPublished: boolean;
  budgetAlerts: boolean; weeklyReports: boolean; emailDigest: boolean;
  browserPush: boolean; smsAlerts: boolean;
}

interface PlatformKeys {
  platform: string; label: string; configured: number; total: number;
  keys: { keyName: string; configured: boolean; isActive: boolean; id?: string }[];
}

const SETTINGS_SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  TIKTOK_CONNECTION_MODE: [
    { value: "sync", label: "Sync only" },
    { value: "upload", label: "Upload / draft mode" },
    { value: "publish", label: "Direct publish" },
  ],
  X_PUBLISH_MODE: [
    { value: "auto", label: "Auto publish" },
    { value: "manual", label: "Manual only" },
  ],
  MIGRATECK_PLATFORM_DEFAULT_PRODUCT: [
    { value: "MT", label: "MT" },
    { value: "MH", label: "MH" },
    { value: "MP", label: "MP" },
    { value: "MV", label: "MV" },
    { value: "MM", label: "MM" },
    { value: "MI", label: "MI" },
    { value: "MK", label: "MK" },
  ],
};

const SETTINGS_KEY_HELP: Record<string, string> = {
  TIKTOK_CONNECTION_MODE: "Controls which TikTok OAuth scopes MigraMarket requests: sync-only, upload/draft, or direct publish.",
  X_PUBLISH_MODE: "Controls whether X is auto-published by MigraMarket or treated as manual-dispatch only.",
  MIGRATECK_PLATFORM_PROVISION_URL: "Internal MigraTeck receiver endpoint for revenue provisioning handoffs.",
  MIGRATECK_PLATFORM_PROVISION_TOKEN: "Shared bearer token used by MigraMarket to authenticate handoffs into MigraTeck.",
  MIGRATECK_PLATFORM_DEFAULT_PRODUCT: "Default product code used when a revenue plan does not map to a more specific product.",
};

interface BillingData {
  plan: string; billingEmail?: string; taxId?: string; memberSince?: string;
  usage: { users: number; campaigns: number; platforms: number; content: number };
  limits: { maxUsers: number; maxPlatforms: number; price: number; hasAI: boolean; hasSEO: boolean; hasEmail: boolean };
  plans: Record<string, { maxUsers: number; maxPlatforms: number; price: number; hasAI: boolean; hasSEO: boolean; hasEmail: boolean }>;
}

interface AuditEntry {
  id: string; action: string; entity: string; details?: string; createdAt: string;
  user: { name: string; email: string };
}

interface RevenueProvisionSmokeResult {
  ok: boolean;
  environment: {
    endpoint: string;
    tokenConfigured: boolean;
  };
  organizationId: string;
  actorUserId: string;
  customer: {
    id: string;
    company: string;
    plan: string;
    tenantId?: string | null;
    monthlyRevenue: number;
    onboardingStatus: string;
  };
  refreshedCustomer?: {
    id: string;
    onboardingStatus: string;
    updatedAt: string;
  } | null;
  latestHandoff?: {
    id: string;
    status: string;
    targetSystem: string;
    endpoint: string | null;
    errorMessage: string | null;
    requestedAt: string;
    completedAt: string | null;
  } | null;
  provisioning: {
    status: string;
    httpStatus?: number;
    reason?: string;
    response?: any;
  };
}

// ─── Toast helper ───────────────────────────────────────
function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium animate-in slide-in-from-bottom ${type === "success" ? "bg-green-600" : "bg-red-600"}`}>
      {type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ─── Toggle Switch ──────────────────────────────────────
function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-200"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function Settings() {
  const { user, organization, updateUser, updateOrganization } = useAuth();
  const [tab, setTab] = useState("profile");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "organization", label: "Organization", icon: Building2 },
    { id: "api-keys", label: "API Keys", icon: Key },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "billing", label: "Billing", icon: CreditCard },
    { id: "security", label: "Security", icon: Shield },
    { id: "audit-log", label: "Audit Log", icon: ScrollText },
  ];

  const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account and platform settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <nav className="space-y-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>

        <div className="lg:col-span-3">
          {tab === "profile" && <ProfileTab showToast={showToast} updateUser={updateUser} />}
          {tab === "organization" && <OrganizationTab showToast={showToast} updateOrganization={updateOrganization} />}
          {tab === "api-keys" && <ApiKeysTab showToast={showToast} />}
          {tab === "notifications" && <NotificationsTab showToast={showToast} />}
          {tab === "billing" && <BillingTab showToast={showToast} />}
          {tab === "security" && <SecurityTab showToast={showToast} />}
          {tab === "audit-log" && <AuditLogTab />}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// PROFILE TAB
// ═══════════════════════════════════════════════════════
function ProfileTab({ showToast, updateUser }: { showToast: (m: string, t?: "success" | "error") => void; updateUser: (d: any) => void }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", timezone: "", language: "" });

  useEffect(() => {
    api.get<Profile>("/settings/profile").then((p) => {
      setProfile(p);
      setForm({ name: p.name, phone: p.phone || "", timezone: p.timezone || "America/New_York", language: p.language || "en" });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patch<Profile>("/settings/profile", form);
      setProfile(updated);
      updateUser({ name: updated.name, phone: updated.phone, timezone: updated.timezone, language: updated.language });
      showToast("Profile updated successfully");
    } catch (err: any) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="card p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const timezones = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Anchorage", "Pacific/Honolulu", "America/Puerto_Rico", "America/Port-au-Prince",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Tokyo", "Asia/Shanghai",
    "Australia/Sydney", "America/Sao_Paulo", "Africa/Lagos",
  ];

  return (
    <div className="space-y-6">
      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold">Profile Settings</h2>

        {/* Avatar + info */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
            {profile.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{profile.name}</p>
            <p className="text-sm text-gray-500">{profile.email}</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
              {profile.role === "owner" && <Crown className="w-3 h-3" />}
              {profile.role}
            </span>
          </div>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Full Name</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input bg-gray-50" value={profile.email} disabled />
          </div>
          <div>
            <label className="label">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className="input pl-10" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
            </div>
          </div>
          <div>
            <label className="label">Timezone</label>
            <select className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              {timezones.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Language</label>
            <select className="input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="ht">Kreyòl Ayisyen</option>
              <option value="pt">Português</option>
            </select>
          </div>
          <div>
            <label className="label">Member Since</label>
            <input className="input bg-gray-50" value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : ""} disabled />
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// ORGANIZATION TAB
// ═══════════════════════════════════════════════════════
function OrganizationTab({ showToast, updateOrganization }: { showToast: (m: string, t?: "success" | "error") => void; updateOrganization: (d: any) => void }) {
  const { user } = useAuth();
  const [org, setOrg] = useState<OrgData | null>(null);
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "member" });
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ name: "", website: "", industry: "", description: "", address: "", phone: "", billingEmail: "", taxId: "" });

  const load = useCallback(() => {
    api.get<OrgData>("/settings/organization").then((o) => {
      setOrg(o);
      setForm({
        name: o.name, website: o.website || "", industry: o.industry || "",
        description: o.description || "", address: o.address || "",
        phone: o.phone || "", billingEmail: o.billingEmail || "", taxId: o.taxId || "",
      });
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patch<OrgData>("/settings/organization", form);
      updateOrganization({ name: updated.name, website: updated.website, industry: updated.industry, description: updated.description });
      showToast("Organization updated");
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  const invite = async () => {
    setInviting(true);
    try {
      const res = await api.post<{ tempPassword: string }>("/settings/organization/invite", inviteForm);
      showToast(`Invited ${inviteForm.email} — temp password: ${res.tempPassword}`);
      setShowInvite(false);
      setInviteForm({ email: "", name: "", role: "member" });
      load();
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setInviting(false); }
  };

  const removeMember = async (id: string) => {
    if (!confirm("Remove this member?")) return;
    try {
      await api.delete(`/settings/organization/members/${id}`);
      showToast("Member removed"); load();
    } catch (err: any) { showToast(err.message, "error"); }
  };

  const changeRole = async (id: string, role: string) => {
    try {
      await api.patch(`/settings/organization/members/${id}/role`, { role });
      showToast("Role updated"); load();
    } catch (err: any) { showToast(err.message, "error"); }
  };

  if (!org) return <div className="card p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const isAdmin = ["owner", "admin"].includes(user?.role || "");

  return (
    <div className="space-y-6">
      {/* Org details */}
      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold">Organization Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Organization Name</label>
            <div className="relative"><Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!isAdmin} /></div>
          </div>
          <div>
            <label className="label">Slug</label>
            <input className="input bg-gray-50" value={org.slug} disabled />
          </div>
          <div>
            <label className="label">Website</label>
            <div className="relative"><Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yoursite.com" disabled={!isAdmin} /></div>
          </div>
          <div>
            <label className="label">Industry</label>
            <select className="input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} disabled={!isAdmin}>
              <option value="">Select...</option>
              {["Technology", "E-commerce", "Healthcare", "Finance", "Education", "Real Estate", "Hospitality", "Marketing Agency", "Non-Profit", "Other"].map((i) => (
                <option key={i} value={i.toLowerCase()}>{i}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Description</label>
            <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!isAdmin} />
          </div>
          <div>
            <label className="label">Address</label>
            <div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!isAdmin} /></div>
          </div>
          <div>
            <label className="label">Phone</label>
            <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={!isAdmin} /></div>
          </div>
          <div>
            <label className="label">Billing Email</label>
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.billingEmail} onChange={(e) => setForm({ ...form, billingEmail: e.target.value })} disabled={!isAdmin} /></div>
          </div>
          <div>
            <label className="label">Tax ID / EIN</label>
            <div className="relative"><FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="input pl-10" value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} disabled={!isAdmin} /></div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex justify-end">
            <button onClick={save} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
          </div>
        )}
      </div>

      {/* Team Members */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Team Members ({org.members.length})</h2>
          {isAdmin && (
            <button onClick={() => setShowInvite(!showInvite)} className="btn-primary text-sm">
              <UserPlus className="w-4 h-4" /> Invite Member
            </button>
          )}
        </div>

        {/* Invite form */}
        {showInvite && (
          <div className="bg-blue-50 rounded-lg p-4 space-y-3 border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className="input" placeholder="Name" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} />
              <input className="input" placeholder="Email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
              <select className="input" value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={invite} disabled={inviting} className="btn-primary text-sm">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Send Invite
              </button>
              <button onClick={() => setShowInvite(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        )}

        {/* Members list */}
        <div className="divide-y divide-gray-100">
          {org.members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                  {m.name[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{m.name}</p>
                  <p className="text-xs text-gray-400">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  m.role === "owner" ? "bg-amber-50 text-amber-700" :
                  m.role === "admin" ? "bg-purple-50 text-purple-700" :
                  m.role === "viewer" ? "bg-gray-100 text-gray-600" :
                  "bg-blue-50 text-blue-700"
                }`}>
                  {m.role}
                </span>
                {isAdmin && m.id !== user?.id && m.role !== "owner" && (
                  <>
                    <select
                      className="text-xs border border-gray-200 rounded px-1.5 py-1"
                      value={m.role}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button onClick={() => removeMember(m.id)} className="p-1 text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// API KEYS TAB
// ═══════════════════════════════════════════════════════
function ApiKeysTab({ showToast }: { showToast: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState<PlatformKeys[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ platform: string; keyName: string } | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, string>>({});
  const [runningSmoke, setRunningSmoke] = useState(false);
  const [smokeResult, setSmokeResult] = useState<RevenueProvisionSmokeResult | null>(null);

  const beginEditing = (platform: string, keyName: string) => {
    setEditing({ platform, keyName });
    const options = SETTINGS_SELECT_OPTIONS[keyName];
    setKeyInput(options?.[0]?.value || "");
  };

  const load = useCallback(async () => {
    try {
      const data = await api.get<PlatformKeys[]>("/settings/api-keys");
      setPlatforms(data);
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const saveKey = async (platform: string, keyName: string) => {
    if (!keyInput.trim()) return;
    setSaving(true);
    try {
      await api.put("/settings/api-keys", { platform, keyName, keyValue: keyInput });
      showToast(`${keyName} saved`);
      setEditing(null);
      setKeyInput("");
      load();
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  };

  const deleteKey = async (id: string) => {
    if (!confirm("Delete this API key?")) return;
    try {
      await api.delete(`/settings/api-keys/${id}`);
      showToast("Key deleted");
      load();
    } catch (err: any) { showToast(err.message, "error"); }
  };

  const revealKey = async (id: string) => {
    try {
      const data = await api.get<{ fullValue: string }>(`/settings/api-keys/${id}/reveal`);
      setRevealedKeys((prev) => ({ ...prev, [id]: data.fullValue }));
      setTimeout(() => setRevealedKeys((prev) => { const n = { ...prev }; delete n[id]; return n; }), 10000);
    } catch (err: any) { showToast(err.message, "error"); }
  };

  const runProvisionSmoke = async () => {
    setRunningSmoke(true);
    try {
      const result = await api.post<RevenueProvisionSmokeResult>("/settings/ops/revenue-provision-smoke");
      setSmokeResult(result);
      showToast(result.ok ? "Revenue provisioning smoke passed" : "Revenue provisioning smoke completed with issues", result.ok ? "success" : "error");
    } catch (err: any) {
      showToast(err.message, "error");
    } finally {
      setRunningSmoke(false);
    }
  };

  if (loading) return <div className="card p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const selectOptions = editing ? SETTINGS_SELECT_OPTIONS[editing.keyName] : undefined;

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-1">API Keys & Platform Connections</h2>
        <p className="text-sm text-gray-500 mb-6">Configure API keys for each platform to enable direct posting, analytics, and AI features.</p>

        <div className="space-y-3">
          {platforms.map((p) => (
            <div key={p.platform} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpanded(expanded === p.platform ? null : p.platform)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${p.configured > 0 ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                    <Key className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{p.label}</p>
                    <p className="text-xs text-gray-400">{p.configured}/{p.total} keys configured</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.configured === p.total && p.total > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full">Connected</span>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded === p.platform ? "rotate-180" : ""}`} />
                </div>
              </button>

              {expanded === p.platform && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50">
                  {p.keys.map((k) => (
                    <div key={k.keyName} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${k.configured ? "bg-green-500" : "bg-gray-300"}`} />
                        <div>
                          <code className="text-xs text-gray-600 font-mono">{k.keyName}</code>
                          {SETTINGS_KEY_HELP[k.keyName] && (
                            <p className="text-[10px] text-gray-400 mt-0.5">{SETTINGS_KEY_HELP[k.keyName]}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {k.configured && k.id ? (
                          <>
                            {revealedKeys[k.id] ? (
                              <code className="text-xs text-gray-500 font-mono max-w-[200px] truncate">{revealedKeys[k.id]}</code>
                            ) : (
                              <button onClick={() => revealKey(k.id!)} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Reveal
                              </button>
                            )}
                            <button onClick={() => beginEditing(p.platform, k.keyName)} className="text-xs text-gray-500 hover:text-gray-700">
                              <RefreshCw className="w-3 h-3" />
                            </button>
                            <button onClick={() => deleteKey(k.id!)} className="text-xs text-red-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <button onClick={() => beginEditing(p.platform, k.keyName)} className="text-xs text-blue-600 hover:text-blue-700">
                            Configure
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Inline editor */}
                  {editing && editing.platform === p.platform && (
                    <div className="mt-2 flex items-center gap-2 bg-white rounded-lg p-2 border border-blue-200">
                      <code className="text-xs text-gray-500 font-mono whitespace-nowrap">{editing.keyName}=</code>
                      {selectOptions ? (
                        <select
                          className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 focus:border-blue-500 outline-none"
                          value={keyInput || selectOptions[0].value}
                          onChange={(e) => setKeyInput(e.target.value)}
                          autoFocus
                        >
                          {selectOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 focus:border-blue-500 outline-none"
                          type="password"
                          placeholder="Paste key value..."
                          value={keyInput}
                          onChange={(e) => setKeyInput(e.target.value)}
                          autoFocus
                        />
                      )}
                      <button onClick={() => saveKey(p.platform, editing.keyName)} disabled={saving} className="btn-primary text-xs py-1">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                      </button>
                      <button onClick={() => setEditing(null)} className="btn-secondary text-xs py-1"><X className="w-3 h-3" /></button>
                    </div>
                  )}

                  {p.platform === "internal" && (
                    <div className="mt-4 rounded-lg border border-blue-200 bg-white p-4 space-y-3">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Revenue Provisioning Smoke</p>
                          <p className="text-xs text-gray-500">
                            Triggers a real `revenue.close_deal` handoff through the configured MigraTeck bridge and records the result.
                          </p>
                        </div>
                        <button
                          onClick={runProvisionSmoke}
                          disabled={runningSmoke || !["owner", "admin"].includes(user?.role || "")}
                          className="btn-primary text-xs py-2"
                        >
                          {runningSmoke ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                          Run Smoke
                        </button>
                      </div>

                      {smokeResult && (
                        <div className={`rounded-lg border p-3 text-xs space-y-2 ${smokeResult.ok ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${smokeResult.ok ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                              {smokeResult.ok ? "Delivered" : "Attention Required"}
                            </span>
                            <span className="text-gray-600">Customer: <code className="font-mono">{smokeResult.customer.company}</code></span>
                            <span className="text-gray-600">Plan: <code className="font-mono">{smokeResult.customer.plan}</code></span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-600">
                            <p>Endpoint: <code className="font-mono break-all">{smokeResult.environment.endpoint}</code></p>
                            <p>Handoff: <code className="font-mono">{smokeResult.latestHandoff?.id || "n/a"}</code></p>
                            <p>Customer status: <code className="font-mono">{smokeResult.refreshedCustomer?.onboardingStatus || smokeResult.customer.onboardingStatus}</code></p>
                            <p>HTTP: <code className="font-mono">{smokeResult.provisioning.httpStatus || "n/a"}</code></p>
                          </div>
                          {smokeResult.latestHandoff?.errorMessage && (
                            <p className="text-red-600">Error: {smokeResult.latestHandoff.errorMessage}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════
function NotificationsTab({ showToast }: { showToast: (m: string, t?: "success" | "error") => void }) {
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<NotifPrefs>("/settings/notifications").then(setPrefs);
  }, []);

  const toggle = async (key: keyof NotifPrefs) => {
    if (!prefs) return;
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try {
      await api.patch("/settings/notifications", { [key]: updated[key] });
      showToast("Preferences saved");
    } catch (err: any) { showToast(err.message, "error"); setPrefs(prefs); }
    finally { setSaving(false); }
  };

  if (!prefs) return <div className="card p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const sections = [
    {
      title: "Campaign & Content",
      items: [
        { key: "campaignCompleted" as keyof NotifPrefs, label: "Campaign completed", desc: "When a campaign finishes running" },
        { key: "postPublished" as keyof NotifPrefs, label: "Post published", desc: "When scheduled posts go live" },
      ],
    },
    {
      title: "Leads & Engagement",
      items: [
        { key: "newLeads" as keyof NotifPrefs, label: "New leads", desc: "When a contact form is submitted" },
        { key: "budgetAlerts" as keyof NotifPrefs, label: "Budget alerts", desc: "When ad spend reaches budget limits" },
      ],
    },
    {
      title: "Reports & Digests",
      items: [
        { key: "weeklyReports" as keyof NotifPrefs, label: "Weekly reports", desc: "Receive weekly performance summaries" },
        { key: "emailDigest" as keyof NotifPrefs, label: "Daily email digest", desc: "Get a daily summary of activity via email" },
      ],
    },
    {
      title: "Delivery Methods",
      items: [
        { key: "browserPush" as keyof NotifPrefs, label: "Browser push notifications", desc: "Receive push notifications in your browser" },
        { key: "smsAlerts" as keyof NotifPrefs, label: "SMS alerts", desc: "Get critical alerts via text message" },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {sections.map((s) => (
        <div key={s.title} className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold">{s.title}</h2>
          {s.items.map((item) => (
            <div key={item.key} className="flex items-center justify-between py-1">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-400">{item.desc}</p>
              </div>
              <Toggle checked={prefs[item.key]} onChange={() => toggle(item.key)} disabled={saving} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// BILLING TAB
// ═══════════════════════════════════════════════════════
function BillingTab({ showToast }: { showToast: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  useEffect(() => {
    api.get<BillingData>("/settings/billing").then(setBilling);
  }, []);

  const changePlan = async (plan: string) => {
    if (!confirm(`Switch to the ${plan} plan?`)) return;
    setUpgrading(plan);
    try {
      await api.patch("/settings/billing/plan", { plan });
      const updated = await api.get<BillingData>("/settings/billing");
      setBilling(updated);
      showToast(`Switched to ${plan} plan`);
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setUpgrading(null); }
  };

  if (!billing) return <div className="card p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const planColors: Record<string, string> = { free: "border-gray-200", pro: "border-blue-500 ring-1 ring-blue-500", enterprise: "border-purple-500 ring-1 ring-purple-500" };
  const planFeatures: Record<string, string[]> = {
    free: ["3 platforms", "100 posts/mo", "Basic analytics", "1 user"],
    pro: ["All platforms", "Unlimited posts", "AI generation", "5 users", "SEO tools", "Email marketing"],
    enterprise: ["Everything in Pro", "White label", "API access", "Unlimited users", "Priority support", "Custom integrations"],
  };

  return (
    <div className="space-y-6">
      {/* Usage */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold">Current Usage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Team Members", value: billing.usage.users, limit: billing.limits.maxUsers },
            { label: "Campaigns", value: billing.usage.campaigns, limit: null },
            { label: "Platforms", value: billing.usage.platforms, limit: billing.limits.maxPlatforms },
            { label: "Content Items", value: billing.usage.content, limit: null },
          ].map((u) => (
            <div key={u.label} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-400">{u.label}</p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {u.value}{u.limit && u.limit < 999 && <span className="text-sm font-normal text-gray-400">/{u.limit}</span>}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Plans */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold">Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["free", "pro", "enterprise"].map((planId) => {
            const isCurrent = billing.plan === planId;
            const price = billing.plans[planId]?.price ?? 0;
            return (
              <div key={planId} className={`rounded-xl border-2 p-5 ${isCurrent ? planColors[planId] : "border-gray-200"}`}>
                {isCurrent && <span className="text-xs font-medium text-blue-600 mb-2 block">Current Plan</span>}
                <h3 className="font-semibold text-lg capitalize">{planId}</h3>
                <p className="text-3xl font-bold mt-1">${price}<span className="text-sm font-normal text-gray-400">/mo</span></p>
                <ul className="mt-4 space-y-2">
                  {(planFeatures[planId] || []).map((f) => (
                    <li key={f} className="text-sm text-gray-600 flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => !isCurrent && user?.role === "owner" && changePlan(planId)}
                  disabled={isCurrent || user?.role !== "owner" || upgrading === planId}
                  className={`w-full mt-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isCurrent ? "bg-gray-100 text-gray-400 cursor-default" :
                    "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {upgrading === planId ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isCurrent ? "Current Plan" : "Switch Plan"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SECURITY TAB
// ═══════════════════════════════════════════════════════
// ─── Password Strength Helper ───────────────────────────
function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: "Weak", color: "bg-red-500" };
  if (score <= 2) return { score, label: "Fair", color: "bg-orange-500" };
  if (score <= 3) return { score, label: "Good", color: "bg-yellow-500" };
  if (score <= 4) return { score, label: "Strong", color: "bg-blue-500" };
  return { score, label: "Excellent", color: "bg-green-500" };
}

interface SessionEntry {
  id: string; ipAddress?: string; userAgent?: string;
  lastActiveAt: string; createdAt: string;
}

function parseUserAgent(ua?: string): { browser: string; os: string; icon: string } {
  if (!ua) return { browser: "Unknown", os: "Unknown", icon: "🌐" };
  let browser = "Unknown", os = "Unknown", icon = "🌐";

  if (/Chrome\//.test(ua) && !/Edg/.test(ua)) { browser = "Chrome"; icon = "🟢"; }
  else if (/Edg\//.test(ua)) { browser = "Edge"; icon = "🔵"; }
  else if (/Firefox\//.test(ua)) { browser = "Firefox"; icon = "🟠"; }
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) { browser = "Safari"; icon = "🔷"; }
  else if (/curl|wget|Postman|httpie/i.test(ua)) { browser = "API Client"; icon = "⚡"; }

  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";

  return { browser, os, icon };
}

function SecurityTab({ showToast }: { showToast: (m: string, t?: "success" | "error") => void }) {
  const { user, updateUser } = useAuth();
  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // 2FA
  const [twoFA, setTwoFA] = useState(user?.twoFactorEnabled || false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState<string | null>(null);
  const [twoFAQrCode, setTwoFAQrCode] = useState<string | null>(null);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");

  // Sessions
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);
  const [revokePassword, setRevokePassword] = useState("");
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  // Audit Log
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const pwStrength = getPasswordStrength(pwForm.newPassword);

  // ─── Change Password ─────────────────────────────────
  const changePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      showToast("Passwords do not match", "error"); return;
    }
    if (pwForm.newPassword.length < 8) {
      showToast("Password must be at least 8 characters", "error"); return;
    }
    if (pwForm.currentPassword === pwForm.newPassword) {
      showToast("New password must be different from current", "error"); return;
    }
    setChangingPw(true);
    try {
      await api.post("/settings/security/change-password", {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      showToast("Password changed successfully");
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowCurrentPw(false);
      setShowNewPw(false);
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setChangingPw(false); }
  };

  // ─── Enable 2FA ───────────────────────────────────────
  const enable2FA = async () => {
    setToggling2FA(true);
    try {
      const res = await api.post<{ secret: string; qrCode: string; otpauthUri: string }>("/settings/security/enable-2fa");
      setTwoFA(true);
      setTwoFASecret(res.secret);
      setTwoFAQrCode(res.qrCode);
      setShow2FAModal(true);
      updateUser({ twoFactorEnabled: true });
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setToggling2FA(false); }
  };

  const close2FAModal = () => {
    setShow2FAModal(false);
    setShowManualEntry(false);
    setVerifyCode("");
    showToast("Two-factor authentication enabled");
  };

  // ─── Disable 2FA ──────────────────────────────────────
  const disable2FA = async () => {
    if (!disablePassword) { showToast("Enter your password to confirm", "error"); return; }
    setToggling2FA(true);
    try {
      await api.post("/settings/security/disable-2fa", { password: disablePassword });
      setTwoFA(false);
      setTwoFASecret(null);
      setTwoFAQrCode(null);
      updateUser({ twoFactorEnabled: false });
      setShowDisable2FA(false);
      setDisablePassword("");
      showToast("Two-factor authentication disabled");
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setToggling2FA(false); }
  };

  // ─── Load Sessions ────────────────────────────────────
  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const data = await api.get<SessionEntry[]>("/settings/security/sessions");
      setSessions(data);
      setShowSessions(true);
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setLoadingSessions(false); }
  };

  // ─── Revoke All Sessions ──────────────────────────────
  const revokeAllSessions = async () => {
    if (!revokePassword) { showToast("Enter your password to confirm", "error"); return; }
    setRevokingAll(true);
    try {
      await api.post("/settings/security/revoke-sessions", { password: revokePassword });
      setSessions([]);
      setShowRevokeConfirm(false);
      setRevokePassword("");
      showToast("All sessions revoked successfully");
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setRevokingAll(false); }
  };

  // ─── Load Audit Log ───────────────────────────────────
  const loadAuditLog = async () => {
    setLoadingAudit(true);
    try {
      const data = await api.get<AuditEntry[]>("/settings/security/audit-log");
      setAuditLog(data);
      setShowAudit(true);
    } catch (err: any) { showToast(err.message, "error"); }
    finally { setLoadingAudit(false); }
  };

  const copySecret = () => {
    if (twoFASecret) {
      navigator.clipboard.writeText(twoFASecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const timeAgo = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const actionLabels: Record<string, { label: string; color: string; icon: string }> = {
    change_password: { label: "Password Changed", color: "text-blue-700 bg-blue-50", icon: "🔑" },
    enable_2fa: { label: "2FA Enabled", color: "text-green-700 bg-green-50", icon: "🛡️" },
    disable_2fa: { label: "2FA Disabled", color: "text-red-700 bg-red-50", icon: "⚠️" },
    revoke_all_sessions: { label: "Sessions Revoked", color: "text-orange-700 bg-orange-50", icon: "🔒" },
    update_profile: { label: "Profile Updated", color: "text-purple-700 bg-purple-50", icon: "👤" },
    update_organization: { label: "Organization Updated", color: "text-indigo-700 bg-indigo-50", icon: "🏢" },
    invite_member: { label: "Member Invited", color: "text-cyan-700 bg-cyan-50", icon: "📨" },
    remove_member: { label: "Member Removed", color: "text-red-700 bg-red-50", icon: "🚫" },
    configure_api_key: { label: "API Key Configured", color: "text-violet-700 bg-violet-50", icon: "🔧" },
    delete_api_key: { label: "API Key Deleted", color: "text-red-700 bg-red-50", icon: "🗑️" },
    toggle_api_key: { label: "API Key Toggled", color: "text-yellow-700 bg-yellow-50", icon: "🔄" },
    change_plan: { label: "Plan Changed", color: "text-emerald-700 bg-emerald-50", icon: "💳" },
    login: { label: "Login", color: "text-green-700 bg-green-50", icon: "✅" },
  };

  return (
    <div className="space-y-6">
      {/* ───── Change Password ───── */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold">Change Password</h2>
        </div>
        <div className="space-y-3 max-w-md">
          <div>
            <label className="label">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPw ? "text" : "password"}
                className="input pr-10"
                placeholder="Enter current password"
                value={pwForm.currentPassword}
                onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPw(!showCurrentPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">New Password</label>
            <div className="relative">
              <input
                type={showNewPw ? "text" : "password"}
                className="input pr-10"
                placeholder="Enter new password (min 8 chars)"
                value={pwForm.newPassword}
                onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowNewPw(!showNewPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* Strength meter */}
            {pwForm.newPassword && (
              <div className="mt-2 space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= pwStrength.score ? pwStrength.color : "bg-gray-200"}`} />
                  ))}
                </div>
                <p className={`text-xs font-medium ${pwStrength.score <= 1 ? "text-red-600" : pwStrength.score <= 2 ? "text-orange-600" : pwStrength.score <= 3 ? "text-yellow-600" : pwStrength.score <= 4 ? "text-blue-600" : "text-green-600"}`}>
                  {pwStrength.label}
                  <span className="text-gray-400 font-normal ml-1">
                    — {pwForm.newPassword.length < 8 ? "min 8 chars" : pwForm.newPassword.length < 12 ? "try 12+ chars" : ""} {!/[A-Z]/.test(pwForm.newPassword) ? "add uppercase" : ""} {!/[0-9]/.test(pwForm.newPassword) ? "add number" : ""} {!/[^A-Za-z0-9]/.test(pwForm.newPassword) ? "add symbol" : ""}
                  </span>
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              className={`input ${pwForm.confirmPassword && pwForm.confirmPassword !== pwForm.newPassword ? "border-red-300 focus:ring-red-500" : ""}`}
              placeholder="Confirm new password"
              value={pwForm.confirmPassword}
              onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
            />
            {pwForm.confirmPassword && pwForm.confirmPassword !== pwForm.newPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
          <button
            onClick={changePassword}
            disabled={changingPw || !pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword || pwForm.newPassword !== pwForm.confirmPassword || pwForm.newPassword.length < 8}
            className="btn-primary"
          >
            {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update Password
          </button>
        </div>
      </div>

      {/* ═══ 2FA QR Code Modal ═══ */}
      {show2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close2FAModal} />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-0 overflow-hidden animate-in zoom-in-95 fade-in">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Set Up 2FA</h3>
                    <p className="text-sm text-white/80">Scan the QR code below</p>
                  </div>
                </div>
                <button onClick={close2FAModal} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">1</span>
                <p className="text-sm text-gray-600">Open your authenticator app (Google Authenticator, Authy, or 1Password) and scan this QR code:</p>
              </div>

              {/* QR Code */}
              {twoFAQrCode && (
                <div className="flex justify-center">
                  <div className="bg-white border-2 border-gray-100 rounded-xl p-3 shadow-sm">
                    <img src={twoFAQrCode} alt="2FA QR Code" className="w-56 h-56" />
                  </div>
                </div>
              )}

              {/* Manual entry toggle */}
              <div>
                <button
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                >
                  <Key className="w-3 h-3" />
                  {showManualEntry ? "Hide manual entry key" : "Can't scan? Enter key manually"}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showManualEntry ? "rotate-180" : ""}`} />
                </button>

                {showManualEntry && twoFASecret && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center">A</span>
                      <p className="text-xs text-gray-500">Account: <span className="font-medium text-gray-700">{user?.email}</span></p>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 p-3">
                      <code className="text-sm font-mono text-gray-900 flex-1 break-all select-all tracking-wider">
                        {twoFASecret.match(/.{1,4}/g)?.join(" ")}
                      </code>
                      <button onClick={copySecret} className="flex-shrink-0 px-2 py-1.5 text-xs bg-white hover:bg-gray-100 border border-gray-200 rounded-md text-gray-600 flex items-center gap-1 transition-colors">
                        {secretCopied ? <><Check className="w-3 h-3 text-green-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Type: TOTP · Algorithm: SHA-1 · Digits: 6 · Period: 30s</p>
                  </div>
                )}
              </div>

              {/* Step 2 - Verify */}
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">2</span>
                <p className="text-sm text-gray-600">Enter the 6-digit code from your authenticator app to verify setup:</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  className="input text-center text-2xl font-mono tracking-[0.5em] w-48"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verifyCode.length === 6 && close2FAModal()}
                  autoFocus
                />
                <button
                  onClick={close2FAModal}
                  disabled={verifyCode.length !== 6}
                  className="btn-primary disabled:opacity-40"
                >
                  <Check className="w-4 h-4" /> Verify
                </button>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  <span className="font-medium">Important:</span> Save a copy of the secret key in a safe place. If you lose access to your authenticator app, you'll need this key to recover your account.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">Powered by TOTP (RFC 6238)</p>
              <button onClick={close2FAModal} className="text-sm text-gray-500 hover:text-gray-700 font-medium">
                Skip verification →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── Two-Factor Authentication ───── */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-500 mt-1">Add an extra layer of security to your account</p>
            </div>
          </div>
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${twoFA ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}>
            {twoFA ? "✓ Enabled" : "Disabled"}
          </span>
        </div>

        {twoFA ? (
          <div className="space-y-4">
            {/* Status when enabled */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <p className="text-sm font-medium text-green-800">Two-factor authentication is active</p>
              </div>
              <p className="text-xs text-green-600 mt-1 ml-7">Your account is protected with an authenticator app</p>
            </div>

            {showDisable2FA ? (
              <div className="space-y-3 max-w-md bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Disable Two-Factor Authentication</p>
                    <p className="text-xs text-red-600 mt-1">This will remove the additional security layer from your account. Enter your password to confirm.</p>
                  </div>
                </div>
                <input
                  type="password"
                  className="input"
                  placeholder="Enter your password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && disable2FA()}
                />
                <div className="flex gap-2">
                  <button onClick={disable2FA} disabled={toggling2FA || !disablePassword} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                    {toggling2FA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Disable 2FA
                  </button>
                  <button onClick={() => { setShowDisable2FA(false); setDisablePassword(""); }} className="btn-secondary text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowDisable2FA(true)} className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> Disable Two-Factor Authentication
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-600">
                Two-factor authentication adds a second verification step when you sign in. After enabling, you'll need your authenticator app each time you log in.
              </p>
              <ul className="text-xs text-gray-500 mt-2 space-y-1.5 list-none">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Protects against password theft</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Works with any TOTP authenticator app</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-500" /> Compatible with Google Authenticator, Authy, 1Password</li>
              </ul>
            </div>
            <button onClick={enable2FA} disabled={toggling2FA} className="btn-primary">
              {toggling2FA ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Enable 2FA
            </button>
          </div>
        )}
      </div>

      {/* ───── Active Sessions ───── */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold">Active Sessions</h2>
              <p className="text-sm text-gray-500 mt-1">Manage devices and locations where you're signed in</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showSessions && sessions.length > 0 && (
              <button
                onClick={() => setShowRevokeConfirm(true)}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Revoke All
              </button>
            )}
            <button onClick={loadSessions} disabled={loadingSessions} className="btn-secondary text-sm">
              {loadingSessions ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {showSessions ? "Refresh" : "View Sessions"}
            </button>
          </div>
        </div>

        {/* Revoke confirmation */}
        {showRevokeConfirm && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3 max-w-md">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Revoke All Sessions</p>
                <p className="text-xs text-red-600 mt-1">This will sign out all devices including this one. You'll need to sign in again.</p>
              </div>
            </div>
            <input
              type="password"
              className="input"
              placeholder="Enter your password to confirm"
              value={revokePassword}
              onChange={(e) => setRevokePassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && revokeAllSessions()}
            />
            <div className="flex gap-2">
              <button onClick={revokeAllSessions} disabled={revokingAll || !revokePassword} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {revokingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Revoke All Sessions
              </button>
              <button onClick={() => { setShowRevokeConfirm(false); setRevokePassword(""); }} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        )}

        {showSessions && (
          <div className="space-y-2">
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No active sessions found</p>
                <p className="text-xs text-gray-300 mt-1">Sessions are created when you sign in</p>
              </div>
            ) : (
              sessions.map((session, idx) => {
                const { browser, os, icon } = parseUserAgent(session.userAgent);
                return (
                  <div key={session.id} className={`flex items-center justify-between p-3 rounded-lg border ${idx === 0 ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {browser} on {os}
                          {idx === 0 && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Current</span>}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                          {session.ipAddress && <span>{session.ipAddress}</span>}
                          <span>•</span>
                          <span>Active {timeAgo(session.lastActiveAt)}</span>
                          <span>•</span>
                          <span>Created {new Date(session.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {sessions.length > 0 && (
              <p className="text-xs text-gray-400 text-center mt-2">{sessions.length} active session{sessions.length !== 1 ? "s" : ""}</p>
            )}
          </div>
        )}
      </div>

      {/* ───── Activity Log ───── */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold">Activity Log</h2>
              <p className="text-sm text-gray-500 mt-1">Recent security and account activity</p>
            </div>
          </div>
          <button onClick={loadAuditLog} disabled={loadingAudit} className="btn-secondary text-sm">
            {loadingAudit ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {showAudit ? "Refresh" : "View Log"}
          </button>
        </div>

        {showAudit && (
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {auditLog.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No activity recorded yet</p>
                <p className="text-xs text-gray-300 mt-1">Actions like password changes and settings updates appear here</p>
              </div>
            ) : (
              auditLog.map((entry) => {
                const meta = actionLabels[entry.action] || { label: entry.action.replace(/_/g, " "), color: "text-gray-700 bg-gray-50", icon: "📋" };
                return (
                  <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="text-base mt-0.5">{meta.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="text-xs text-gray-400">{entry.entity}</span>
                      </div>
                      {entry.details && (
                        <p className="text-xs text-gray-400 mt-1 font-mono truncate">{entry.details}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{entry.user?.name || entry.user?.email}</span>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-400">{timeAgo(entry.createdAt)}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-300 whitespace-nowrap flex-shrink-0">
                      {new Date(entry.createdAt).toLocaleString()}
                    </span>
                  </div>
                );
              })
            )}
            {auditLog.length > 0 && (
              <p className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100">
                Showing {auditLog.length} most recent entries
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// AUDIT LOG TAB (Full Viewer)
// ═══════════════════════════════════════════════════════
function AuditLogTab() {
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [entityFilter, setEntityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (entityFilter) params.set("entity", entityFilter);
      if (actionFilter) params.set("action", actionFilter);
      const data = await api.get<any>(`/audit?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, entityFilter, actionFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get<any>("/audit/stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const totalPages = Math.ceil(total / limit);

  const ACTION_COLORS: Record<string, string> = {
    create: "bg-green-50 text-green-700", update: "bg-blue-50 text-blue-700",
    delete: "bg-red-50 text-red-700", login: "bg-purple-50 text-purple-700",
    export: "bg-amber-50 text-amber-700",
  };
  const ENTITY_OPTIONS = ["Campaign", "Content", "EmailCampaign", "Contact", "AdCampaign", "SeoProject", "Automation", "Settings", "User"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit Log</h3>
          <p className="text-sm text-gray-500 mt-0.5">Track all actions performed across your workspace</p>
        </div>
        <button onClick={() => { fetchLogs(); fetchStats(); }}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500">Total Events</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500">Last 7 Days</p>
            <p className="text-2xl font-bold text-blue-600">{stats.recent7d}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500">Entities Tracked</p>
            <p className="text-2xl font-bold text-green-600">{stats.byEntity?.length || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500">Action Types</p>
            <p className="text-2xl font-bold text-purple-600">{stats.byAction?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500 font-medium">Filter:</span>
        </div>
        <select value={entityFilter} onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
          <option value="">All Entities</option>
          {ENTITY_OPTIONS.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <input
          type="text"
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          placeholder="Filter by action..."
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white w-48"
        />
        {(entityFilter || actionFilter) && (
          <button onClick={() => { setEntityFilter(""); setActionFilter(""); setPage(1); }}
            className="text-xs text-red-500 hover:text-red-700">Clear filters</button>
        )}
        <span className="ml-auto text-xs text-gray-400">{total} total entries</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <ScrollText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No audit log entries found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-500">User</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Action</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Entity</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Details</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{log.user.name}</p>
                      <p className="text-xs text-gray-400">{log.user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-600"}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{log.entity}</td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <p className="text-xs text-gray-500 truncate">
                      {typeof log.details === "string" ? log.details : log.details ? JSON.stringify(log.details) : "—"}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = page <= 3 ? i + 1 : page + i - 2;
              if (p < 1 || p > totalPages) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 text-sm rounded-lg ${p === page ? "bg-purple-600 text-white" : "hover:bg-gray-50 text-gray-600"}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
